import {
  Button,
  Checkbox,
  EmptyState,
  Pagination,
  Table,
  type TableSortDirection,
} from '@ntconsult/aluma-react';
import { useTranslation } from 'react-i18next';
import {
  ALLOCATION_COLUMN_LABEL_KEYS,
  type AllocationColumnsState,
} from '../hooks/useAllocationColumns';
import type { AllocationFilterState, ColumnSearchKey } from '../hooks/useAllocationFilters';
import type { AllocationSelection } from '../hooks/useAllocationSelection';
import { usePaginationLabels } from '../hooks/usePaginationLabels';
import { useTagFilterOptions } from '../hooks/useTagFilterOptions';
import type { ProfessionalWithAllocations } from '../types/Allocation';
import type { AllocationSortField } from '../types/Filters';
import type { EditAllocationTarget } from './AllocationModal';
import { AllocationRow } from './AllocationRow';
import type { CloseAllocationTarget } from './CloseAllocationModal';
import { PerPageSelect } from './PerPageSelect';
import {
  COLUMN_MIN_WIDTH,
  PINNED_BLOCK,
  STICKY_END_HEADER,
  WIDE_TABLE_CONTAINER,
} from './wideTable';

const PERCENTAGE_STEP = 5;

type AllocationsTableProps = {
  professionals: ProfessionalWithAllocations[];
  filterState: AllocationFilterState;
  columnsState: AllocationColumnsState;
  page: number;
  perPage: number;
  totalPages: number;
  onPerPageChange: (perPage: number) => void;
  onView: (target: EditAllocationTarget) => void;
  onEdit: (target: EditAllocationTarget) => void;
  onCloseRequest: (target: CloseAllocationTarget) => void;
  groupByProfessional?: boolean;
  selection?: AllocationSelection;
};

export function AllocationsTable({
  professionals,
  filterState,
  columnsState,
  page,
  perPage,
  totalPages,
  onPerPageChange,
  onView,
  onEdit,
  onCloseRequest,
  groupByProfessional = false,
  selection,
}: AllocationsTableProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const paginationLabels = usePaginationLabels();
  const { filters, setSort, setColumnSearch, setPercentageRange, setTags, setBillable } =
    filterState;
  const { isVisible, visibleColumns } = columnsState;
  const tagOptions = useTagFilterOptions(filterState.tags);
  const columnCount = visibleColumns.length + 2 + (selection ? 1 : 0);
  const pageIds = professionals.flatMap((professional) =>
    professional.allocations.map((allocation) => allocation.id),
  );
  const selectedOnPage = selection ? pageIds.filter(selection.isSelected).length : 0;
  const allSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;

  const sortProps = (field: AllocationSortField) => ({
    sortable: true,
    sortDirection: (filters.sortBy === field
      ? filters.sortDirection
      : 'none') as TableSortDirection,
    onSortChange: (next: TableSortDirection) => setSort(field, next),
  });

  const managerFilter = (key: ColumnSearchKey, label: string) => (
    <Table.TextFilter
      label={label}
      placeholder={t('filtros.nomeOuMatriculaPlaceholder')}
      value={filters[key] ?? ''}
      onValueChange={(value) => setColumnSearch(key, value)}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Sem scroll próprio: largura e rolagem horizontal em `wideTable.ts`. */}
      <Table density="compact" stickyHeader containerClassName={WIDE_TABLE_CONTAINER}>
        <Table.Caption className="sr-only">{t('titulo')}</Table.Caption>
        <Table.Head>
          <Table.Row>
            {selection && (
              <Table.Header className="w-px">
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedOnPage > 0 && !allSelected}
                  onCheckedChange={(checked) => selection.setMany(pageIds, checked)}
                  label={<span className="sr-only">{t('lote.selecionarPagina')}</span>}
                />
              </Table.Header>
            )}
            {/* Sem filtro de coluna: o de profissional (múltiplo) fica em "Filtros avançados". */}
            <Table.Header className={COLUMN_MIN_WIDTH.medium} {...sortProps('professional')}>
              {t('tabela.profissional')}
            </Table.Header>
            {isVisible('project') && (
              <Table.Header
                className={COLUMN_MIN_WIDTH.long}
                {...sortProps('project')}
                filter={
                  <Table.TextFilter
                    label={t('tabela.projeto')}
                    placeholder={t('filtros.codigoOuNomePlaceholder')}
                    value={filters.projectSearch ?? ''}
                    onValueChange={(value) => setColumnSearch('projectSearch', value)}
                  />
                }
              >
                {t('tabela.projeto')}
              </Table.Header>
            )}
            {isVisible('contract') && (
              <Table.Header
                className={COLUMN_MIN_WIDTH.long}
                {...sortProps('contract')}
                filter={
                  <Table.TextFilter
                    label={t('tabela.contrato')}
                    placeholder={t('filtros.codigoOuNomePlaceholder')}
                    value={filters.contractSearch ?? ''}
                    onValueChange={(value) => setColumnSearch('contractSearch', value)}
                  />
                }
              >
                {t('tabela.contrato')}
              </Table.Header>
            )}
            {isVisible('client') && (
              <Table.Header
                className={COLUMN_MIN_WIDTH.short}
                {...sortProps('client')}
                filter={
                  <Table.TextFilter
                    label={t('tabela.cliente')}
                    value={filters.clientSearch ?? ''}
                    onValueChange={(value) => setColumnSearch('clientSearch', value)}
                  />
                }
              >
                {t('tabela.cliente')}
              </Table.Header>
            )}
            {isVisible('unit') && (
              <Table.Header align="center" className="w-px whitespace-nowrap">
                {t(ALLOCATION_COLUMN_LABEL_KEYS.unit)}
              </Table.Header>
            )}
            {isVisible('allocatedPercentage') && (
              <Table.Header
                align="right"
                className="w-px whitespace-nowrap"
                {...sortProps('allocatedPercentage')}
                filter={
                  <Table.NumberRangeFilter
                    label={t('tabela.percAlocado')}
                    step={PERCENTAGE_STEP}
                    value={{
                      min: filters.minPercentage ?? null,
                      max: filters.maxPercentage ?? null,
                    }}
                    onValueChange={setPercentageRange}
                  />
                }
              >
                {t('tabela.percAlocado')}
              </Table.Header>
            )}
            {isVisible('billable') && (
              <Table.Header
                align="center"
                className="w-px whitespace-nowrap"
                filter={
                  <Table.OptionsFilter
                    label={t('tabela.faturando')}
                    options={[
                      { value: 'true', label: t('common:yes') },
                      { value: 'false', label: t('common:no') },
                    ]}
                    value={filters.billable === undefined ? [] : [String(filters.billable)]}
                    // Sim e Não marcados juntos = sem filtro.
                    onValueChange={(values) =>
                      setBillable(values.length === 1 ? values[0] === 'true' : null)
                    }
                  />
                }
              >
                {t('tabela.faturando')}
              </Table.Header>
            )}
            {isVisible('costShare') && (
              <Table.Header className="w-px whitespace-nowrap">
                {t(ALLOCATION_COLUMN_LABEL_KEYS.costShare)}
              </Table.Header>
            )}
            {isVisible('salesProfile') && (
              <Table.Header className={COLUMN_MIN_WIDTH.medium}>
                {t(ALLOCATION_COLUMN_LABEL_KEYS.salesProfile)}
              </Table.Header>
            )}
            {isVisible('monthlyCost') && (
              <Table.Header align="right" className="w-px whitespace-nowrap">
                {t(ALLOCATION_COLUMN_LABEL_KEYS.monthlyCost)}
              </Table.Header>
            )}
            {isVisible('allocatedCost') && (
              <Table.Header align="right" className="w-px whitespace-nowrap">
                {t(ALLOCATION_COLUMN_LABEL_KEYS.allocatedCost)}
              </Table.Header>
            )}
            {isVisible('monthlyGrossRevenue') && (
              <Table.Header align="right" className="w-px whitespace-nowrap">
                {t(ALLOCATION_COLUMN_LABEL_KEYS.monthlyGrossRevenue)}
              </Table.Header>
            )}
            {isVisible('monthlyNetRevenue') && (
              <Table.Header align="right" className="w-px whitespace-nowrap">
                {t(ALLOCATION_COLUMN_LABEL_KEYS.monthlyNetRevenue)}
              </Table.Header>
            )}
            {isVisible('ebitdaPercentage') && (
              <Table.Header align="right" className="w-px whitespace-nowrap">
                {t(ALLOCATION_COLUMN_LABEL_KEYS.ebitdaPercentage)}
              </Table.Header>
            )}
            {isVisible('startDate') && (
              <Table.Header
                align="right"
                className="w-px whitespace-nowrap"
                {...sortProps('startDate')}
              >
                {t('tabela.inicio')}
              </Table.Header>
            )}
            {isVisible('endDate') && (
              <Table.Header
                align="right"
                className="w-px whitespace-nowrap"
                {...sortProps('endDate')}
              >
                {t('tabela.fim')}
              </Table.Header>
            )}
            {isVisible('notes') && (
              <Table.Header className={COLUMN_MIN_WIDTH.long}>
                {t(ALLOCATION_COLUMN_LABEL_KEYS.notes)}
              </Table.Header>
            )}
            {isVisible('tags') && (
              <Table.Header
                className={COLUMN_MIN_WIDTH.short}
                filter={
                  <Table.OptionsFilter
                    label={t(ALLOCATION_COLUMN_LABEL_KEYS.tags)}
                    options={tagOptions.map((tag) => ({ value: String(tag.id), label: tag.name }))}
                    value={(filters.tagIds ?? []).map(String)}
                    onValueChange={(values) =>
                      setTags(tagOptions.filter((tag) => values.includes(String(tag.id))))
                    }
                  />
                }
              >
                {t(ALLOCATION_COLUMN_LABEL_KEYS.tags)}
              </Table.Header>
            )}
            {isVisible('accountDirector') && (
              <Table.Header
                className="whitespace-nowrap"
                filter={managerFilter(
                  'accountDirectorSearch',
                  t(ALLOCATION_COLUMN_LABEL_KEYS.accountDirector),
                )}
              >
                {t(ALLOCATION_COLUMN_LABEL_KEYS.accountDirector)}
              </Table.Header>
            )}
            {isVisible('operationsManager') && (
              <Table.Header
                className="whitespace-nowrap"
                filter={managerFilter(
                  'operationManagerSearch',
                  t(ALLOCATION_COLUMN_LABEL_KEYS.operationsManager),
                )}
              >
                {t(ALLOCATION_COLUMN_LABEL_KEYS.operationsManager)}
              </Table.Header>
            )}
            {isVisible('projectManager') && (
              <Table.Header
                className="whitespace-nowrap"
                filter={managerFilter(
                  'projectManagerSearch',
                  t(ALLOCATION_COLUMN_LABEL_KEYS.projectManager),
                )}
              >
                {t(ALLOCATION_COLUMN_LABEL_KEYS.projectManager)}
              </Table.Header>
            )}
            <Table.Header align="right" className={STICKY_END_HEADER}>
              {t('tabela.acoes')}
            </Table.Header>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {professionals.length === 0 ? (
            // Vazio por filtro mantém o cabeçalho: é nele que o filtro se ajusta.
            <Table.Row>
              <Table.Cell colSpan={columnCount}>
                <div className={PINNED_BLOCK}>
                  <EmptyState
                    title={t('estados.filtroSemResultadoTitulo')}
                    description={t('estados.filtroSemResultadoDescricao')}
                    action={
                      filterState.hasActiveFilters && (
                        <Button variant="secondary" onClick={filterState.clearFilters}>
                          {t('filtros.limpar')}
                        </Button>
                      )
                    }
                  />
                </div>
              </Table.Cell>
            </Table.Row>
          ) : (
            professionals.flatMap((professional, professionalIndex) =>
              professional.allocations.map((allocation, index) => (
                <AllocationRow
                  key={allocation.id}
                  professional={professional}
                  allocation={allocation}
                  group={
                    groupByProfessional
                      ? {
                          first: index === 0,
                          size: professional.allocations.length,
                          separated: professionalIndex > 0,
                        }
                      : undefined
                  }
                  isVisible={isVisible}
                  selection={
                    selection && {
                      selected: selection.isSelected(allocation.id),
                      onChange: () => selection.toggle(allocation.id),
                    }
                  }
                  onView={onView}
                  onEdit={onEdit}
                  onCloseRequest={onCloseRequest}
                />
              )),
            )
          )}
        </Table.Body>
      </Table>

      <div className={`flex flex-wrap items-center gap-4 ${PINNED_BLOCK}`}>
        {/* A página é por profissional (`GET /allocations`), não por linha. */}
        <PerPageSelect
          label={t('tabela.profissionaisPorPagina')}
          value={perPage}
          onChange={onPerPageChange}
        />
        {totalPages > 1 && (
          <Pagination
            {...paginationLabels}
            page={page}
            totalPages={totalPages}
            onPageChange={filterState.goToPage}
          />
        )}
      </div>
    </div>
  );
}
